import { createModule } from "saga-slice";
import { call, put } from "redux-saga/effects";
import { createSale, getSales, getSaleById, getTotalSalesToday, getTotalSalesThisWeek, getTotalSalesThisMonth, getTotalSalesThisYear, getTotalSalesForDateRange, getSalesData, search, getSalesReport, downloadBill, generateInvoice, updateSale, generateClientReport, getBusinessReport } from "../services/saleService";
import { t } from "i18next";

const roundToNearestInteger = (num) => {
  return Math.round(num);
};

const saleModule = createModule({
  name: "sales",
  initialState: {
    isLoading: false,
    error: null,
    currentSale: null,
    cart: [],
    sales: [],
    totalVentes: 0,
    totalVentesBL: 0,
    totalVentesDirect: 0,
    totalAchats: 0,
    totalAvoirs: 0,
    totalBlImpayee: 0,
    totalAchatsImpayees: 0,
    totalExpenses: 0,
    totalPaiementsClient: 0,
    totalRetourFournisseur:0,
    totalPaiementsFournisseur:0,
    lowStockItems: [],
    valeurStock:0,
    montantTotalTTC: 0,
    montantTotalHT: 0,
    montantTotalTaxe: 0,
    totalElements: 0, // Total number of records
    totalPages: 0, // Total number of pages
    pageNumber: 0, // Current page number
    pageSize: 30, // Size of each page
    businessReport:null,
    // Client report data
    clientReport: null,
    clientReportLoading: false,
    clientReportError: null,
  },
  selectors: {
    getCart: state => state.sales?.cart || [],
    getTotalAmount: state => state.sales?.totalAmount || 0,
    getIsLoading: state => state.sales?.isLoading || false
  },
  reducers: {
    search: (state) => {
      state.isLoading = true;
    },
    createSale: (state, action) => {
      state.isLoading = true;
      state.error = null;
    },
    getSalesData: (state) => {
      state.isLoading = true;
    },
    getSalesDataSuccess: (state, action) => {
      state.isLoading = false;
      state.totalVentes = action.totalVentes;
      state.totalVentesBL = action.totalVentesBL;
      state.totalAchats = action.totalAchats;
      state.totalAvoirs = action.totalAvoirs;
      state.totalVentesDirect = action.totalVentesDirect;
      state.totalVentesBL = action.totalVentesBL;
      state.totalPaiementsFournisseur = action.totalPaiementsFournisseur;
      state.totalAchatsImpayees = action.totalAchatsImpayees;
      state.lowStockItems = action.lowStockItems;
      state.totalExpenses = action.totalExpenses;
      state.totalBlImpayee = action.totalBlImpayee;
      state.totalRetourFournisseur = action.totalRetourFournisseur;
      state.totalPaiementsClient = action.totalPaiementsClient;
      state.valeurStock = action.valeurStock;
    },
    getSalesDataFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    finishFetching: (state) => {
      state.isLoading = false;
    },
    fetchedData: (state, payload) => {
      state.isLoading = false;
      state.sales = payload.body.content;
      state.totalElements = payload.body.totalElements; // Assuming totalElements is in payload
      state.totalPages = payload.body.totalPages; // Assuming totalPages is in payload
      state.pageNumber = payload.body.pageNumber;
    },
    getTotalSalesToday: (state, action) => {
      state.isLoading = true;
      state.error = null;
    },
    getTotalSalesThisWeek: (state, action) => {
      state.isLoading = true;
      state.error = null;
    },
    getTotalSalesThisMonth: (state, action) => {
      state.isLoading = true;
      state.error = null;
    },
    getTotalSalesThisYear: (state, action) => {
      state.isLoading = true;
      state.error = null;
    },
    createSaleSuccess: (state) => {
      state.isLoading = false;
      state.cart = [];
      state.montantTotalTTC = 0;
      state.montantTotalHT = 0;
      state.montantTotalTaxe = 0;
    },
    createSaleFailure: (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    search: (state) => {
      state.isLoading = true;
    },
    downloadBill: (state) => {
      state.isLoading = true;
    },
    getSalesReport: (state) => {
      state.isLoading = true;
    },
    getBusinessReport: (state) => {
      state.isLoading = true;
    },
    fetchError: (state, action) => {
      state.isLoading = false;
      state.error = "An error occurred";
    },

    addToCart: (state, action) => {
      // --- helpers (keep in sync with your other reducers) ---
      const toNum = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
      const toCents = (v) => Math.round(v * 100);
      const fromCents = (c) => Number((c / 100).toFixed(2));

      // Normalize action (supports action.payload or direct fields)
      const p = action.payload || action;
      const id = p.id;
      const libelle = p.libelle;
      const prixVar = p.prixVariable;
      const unite = p.unite;
      const tvaRate = toNum(p.tauxTVAVente ?? 0.2);                 // default 20%
      const price1TTC = toNum(p.prixVenteTTC1);
      const price2TTC = toNum(p.prixVenteTTC2);
      const price3TTC = toNum(p.prixVenteTTC3);

      // Try to find existing line (string-compare to avoid 1 vs "1")
      const idx = state.cart.findIndex(it => String(it.articleId) === String(id));

      if (idx >= 0) {
        // Update existing
        const it = state.cart[idx];
        it.quantite = toNum(it.quantite) + 1;

        // Unit TTC from existing line if present, else from action
        const unitTTC = toNum(it.prixUnitaireTTC) || price1TTC;
        const unitHT = unitTTC / (1 + tvaRate);

        const lineTTCc = toCents(it.quantite * unitTTC);
        const lineHTc = toCents(it.quantite * unitHT);

        it.prixUnitaireTTC = fromCents(toCents(unitTTC));
        it.prixUnitaireHT = fromCents(toCents(unitHT));
        it.montantTTC = fromCents(lineTTCc);
        it.montantHT = fromCents(lineHTc);
        it.tauxTVA = tvaRate;
      } else {
        // Create new line with qty = 1
        const unitTTC = price1TTC;                 // source of truth
        const unitHT = unitTTC / (1 + tvaRate);

        const lineTTCc = toCents(1 * unitTTC);
        const lineHTc = toCents(1 * unitHT);

        state.cart.push({
          articleId: id,
          articleNom: libelle,
          prixVariable: prixVar,
          prices: [price1TTC, price2TTC, price3TTC],
          quantite: 1,
          prixUnitaireTTC: fromCents(toCents(unitTTC)),
          prixUnitaireHT: fromCents(toCents(unitHT)),
          unite: unite,
          tauxTVA: tvaRate,
          montantHT: fromCents(lineHTc),
          montantTTC: fromCents(lineTTCc),
          // optionally store per-line TVA
          // montantTVA: fromCents(lineTTCc - lineHTc),
        });
      }

      // --- Recompute global totals from primitives (in cents) ---
      let htCumulC = 0;
      let ttcCumulC = 0;
      let taxeTotalC = 0;

      state.cart.forEach(it => {
        const q = toNum(it.quantite);
        const tva = toNum(it.tauxTVA);

        let uTTC = toNum(it.prixUnitaireTTC);
        let uHT;
        if (uTTC > 0) {
          uHT = uTTC / (1 + tva);
        } else {
          uHT = toNum(it.prixUnitaireHT);
          uTTC = uHT * (1 + tva);
        }

        const lineTTCc = toCents(q * uTTC);
        const lineHTc = toCents(q * uHT);

        htCumulC += lineHTc;
        ttcCumulC += lineTTCc;
        taxeTotalC += (lineTTCc - lineHTc);
      });

      state.montantTotalHT = fromCents(htCumulC);
      state.montantTotalTTC = fromCents(ttcCumulC);
      state.montantTotalTaxe = fromCents(taxeTotalC);
    },



    setTotalSales: (state, action) => {
      state.totalSales = action.payload;
    },

    removeFromCart: (state, action) => {
      // Helpers (keep consistent with your other reducers)
      const toNum = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
      const toCents = (v) => Math.round(v * 100);
      const fromCents = (c) => Number((c / 100).toFixed(2));

      const articleIdToRemove = action; // expect payload = articleId
      // If your action sends an index instead, use: const idx = action.payload; state.cart.splice(idx, 1);

      // Remove item(s) by articleId (string compare avoids 1 vs "1")
      state.cart = state.cart.filter(it => String(it.articleId) !== String(articleIdToRemove));

      // Recompute totals in cents from primitives
      let htCumulC = 0;
      let ttcCumulC = 0;
      let taxeTotalC = 0;

      state.cart.forEach(it => {
        const q = toNum(it.quantite);
        const tva = toNum(it.tauxTVA);

        // Source of truth: TTC if provided; otherwise derive from HT
        let uTTC = toNum(it.prixUnitaireTTC);
        let uHT;
        if (uTTC > 0) {
          uHT = uTTC / (1 + tva);
        } else {
          uHT = toNum(it.prixUnitaireHT);
          uTTC = uHT * (1 + tva);
        }

        const lineTTCc = toCents(q * uTTC);
        const lineHTc = toCents(q * uHT);

        htCumulC += lineHTc;
        ttcCumulC += lineTTCc;
        taxeTotalC += (lineTTCc - lineHTc);
      });

      // Set exact-2-decimal totals
      state.montantTotalHT = fromCents(htCumulC);
      state.montantTotalTTC = fromCents(ttcCumulC);
      state.montantTotalTaxe = fromCents(taxeTotalC);
    },


    handleInputChange: (state, action) => {
      const toNum = (v) => {
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : 0;
      };
      const toCents = (v) => Math.round(v * 100);             // number -> integer cents
      const fromCents = (c) => Number((c / 100).toFixed(2));   // integer cents -> number with 2 dp

      const idx = action.index;
      const name = action.name;
      const value = action.value;

      // Update the item (RTK Immer allows "mutating" style)
      const item = { ...state.cart[idx], [name]: value };
      state.cart[idx] = item;

      // Read current line values
      const q = toNum(item.quantite);
      const tva = toNum(item.tauxTVA);

      // Source of truth: TTC if present, else derive from HT
      let unitTTC = toNum(item.prixUnitaireTTC);
      let unitHT;
      if (unitTTC > 0) {
        unitHT = unitTTC / (1 + tva);
      } else {
        unitHT = toNum(item.prixUnitaireHT);
        unitTTC = unitHT * (1 + tva);
      }

      // Line totals in cents (avoid float drift)
      const lineTTCc = toCents(q * unitTTC);
      const lineHTc = toCents(q * unitHT);
      const lineTVAc = lineTTCc - lineHTc;

      // Persist current line (as numbers with exactly 2 decimals)
      state.cart[idx].prixUnitaireHT = fromCents(toCents(unitHT));
      state.cart[idx].montantHT = fromCents(lineHTc);
      state.cart[idx].montantTTC = fromCents(lineTTCc);
      // (Optional) keep per-line TVA if useful
      state.cart[idx].montantTVA = fromCents(lineTVAc);

      // Recompute global totals from primitives for all lines (not from rounded fields)
      let htCumulC = 0;
      let ttcCumulC = 0;
      let taxeTotalC = 0;

      state.cart.forEach((it) => {
        const qq = toNum(it.quantite);
        const ttva = toNum(it.tauxTVA);

        let uTTC = toNum(it.prixUnitaireTTC);
        let uHT;
        if (uTTC > 0) {
          uHT = uTTC / (1 + ttva);
        } else {
          uHT = toNum(it.prixUnitaireHT);
          uTTC = uHT * (1 + ttva);
        }

        const ttcC = toCents(qq * uTTC);
        const htC = toCents(qq * uHT);
        const tvaC = ttcC - htC;

        htCumulC += htC;
        ttcCumulC += ttcC;
        taxeTotalC += tvaC;
      });

      state.montantTotalHT = fromCents(htCumulC);
      state.montantTotalTTC = fromCents(ttcCumulC);
      state.montantTotalTaxe = fromCents(taxeTotalC);
    },


    updateSale: (state) => {
      state.isLoading = true;
    },

    generateInvoice: (state) => {
      state.isLoading = true;
    },

    clearCart: (state) => {
      state.cart = [];
      state.montantTotalTTC = 0;
      state.montantTotalHT = 0;
      state.montantTotalTaxe = 0;
    },
    getBusinessReportSuccess: (state, action) => {
      state.isLoading = false;
      state.businessReport = action;
      state.businessReportError = null;
    },

    generateClientReport: (state) => {
      state.clientReportLoading = true;
      state.clientReportError = null;
    },

    generateClientReportSuccess: (state, action) => {
      state.clientReportLoading = false;
      state.clientReport = action;
      state.clientReportError = null;
    },

    generateClientReportError: (state, action) => {
      state.clientReportLoading = false;
      state.clientReportError = action.payload;
    },

    clearClientReport: (state) => {
      state.clientReport = null;
      state.clientReportError = null;
    }
  },
  sagas: (A) => ({
    *[A.search]({ payload }) {
      try {
        const { data } = yield call(search, payload);
        yield put(A.finishFetching());
        yield put(A.fetchedData(data));
      } catch (e) {
        console.log(e);
        yield put(A.finishFetching());
        yield put(A.fetchError());
      }
    },
    *[A.generateInvoice]({ payload }) {
      try {
        const { data } = yield call(generateInvoice, payload.saleId);
        var byteCharacters = atob(data);
        var byteNumbers = new Array(byteCharacters.length);
        for (var i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        var byteArray = new Uint8Array(byteNumbers);
        var file = new Blob([byteArray], { type: "application/pdf;base64" });
        var fileURL = URL.createObjectURL(file);
        window.open(fileURL);
        yield put(A.finishFetching());
      } catch (error) {
        yield put(A.finishFetching());
        yield put(A.fetchError());
      }
                                                      },
    *[A.updateSale]({ payload }) {
      try {
        const { data } = yield call(updateSale, payload.sale.id, payload.sale);
        yield put(A.finishFetching());
        yield call(payload.onSuccess);
      } catch (e) {
        console.log(e);
        yield put(A.finishFetching());
        yield put(A.fetchError());
      }
    },
    *[A.getBusinessReport]({payload}) {
      try {
        const { data } = yield call(getBusinessReport, payload);
        yield put(A.getBusinessReportSuccess(data));
      } catch (error) {
        yield put(A.getSalesDataFailure(error.message));
      }
    },
    *[A.createSale]({ payload }) {
      try {

        const { data } = yield call(createSale, payload.data);
        yield put(A.createSaleSuccess());

        if (payload.onSuccess) {
          yield call(payload.onSuccess, data);
        }
      } catch (error) {
        yield put(A.createSaleFailure(error.message));
        if (payload.onError) {
          yield call(payload.onError, error.response.data);
        }
      }
    },
    *[A.downloadBill]({ payload }) {
      try {
        console.log(payload);
        const { data } = yield call(downloadBill, payload.id);
        yield put(A.finishFetching());
        yield call(payload.onSuccess, data);
      } catch (e) {
        console.log(e);
        yield put(A.finishFetching());
        yield put(A.fetchError());
      }
    },
    *[A.getSalesData]({ payload }) {
      try {
        const { data } = yield call(getSalesData, payload);
        yield put(A.getSalesDataSuccess(data));
      } catch (error) {
        yield put(A.getSalesDataFailure(error.message));
      }
    },
    *[A.getSalesReport]({ payload }) {
      try {
        const { data } = yield call(getSalesReport, payload.dateRange);
        yield put(A.finishFetching());
        if (payload.onSuccess) {
          yield call(payload.onSuccess, data.body);
        }
      } catch (error) {
        yield put(A.finishFetching());
        yield put(A.getSalesDataFailure(error.message));

      }
    },
    *[A.fetchSales]({ payload }) {
      try {
        const { data } = yield call(getSales, payload.page, payload.size);
        yield put(A.setSales(data.content));
        yield put(A.setPagination({
          currentPage: payload.page,
          totalPages: data.totalPages,
          pageSize: payload.size,
          totalElements: data.totalElements
        }));
      } catch (error) {
        yield put(A.setError(error.message));
      }
    },
    *[A.getTotalSalesToday]() {
      try {
        const { data } = yield call(getTotalSalesToday);
        yield put(A.setTotalSales(data));
      } catch (error) {
        yield put(A.setError(error.message));
      }
    },
    *[A.getTotalSalesThisWeek]() {
      try {
        const { data } = yield call(getTotalSalesThisWeek);
        yield put(A.setTotalSales(data));
      } catch (error) {
        yield put(A.setError(error.message));
      }
    },
    *[A.getTotalSalesThisMonth]() {
      try {
        const { data } = yield call(getTotalSalesThisMonth);
        yield put(A.setTotalSales(data));
      } catch (error) {
        yield put(A.setError(error.message));
      }
    },
    *[A.getTotalSalesThisYear]() {
      try {
        const { data } = yield call(getTotalSalesThisYear);
        yield put(A.setTotalSales(data));
      } catch (error) {
        yield put(A.setError(error.message));
      }
    },
    *[A.getTotalSalesForDateRange]({ payload }) {
      try {
        const { data } = yield call(getTotalSalesForDateRange, payload.startDate, payload.endDate);
        yield put(A.setTotalSales(data));
      } catch (error) {
        yield put(A.setError(error.message));
      }
    },
    
    *[A.generateClientReport]({ payload }) {
      try {
        const { data } = yield call(generateClientReport, payload);
        yield put(A.generateClientReportSuccess(data));
      } catch (error) {
        yield put(A.generateClientReportError(error.message || 'Erreur lors de la génération du rapport'));
      }
    }
  }),

});

export default saleModule;
export const saleActions = saleModule.actions;